/**
 * Laser Ornament Studio - Client LocalStorage Cart Snapshot Hydrator
 * Module: assets/laser-studio-cart-storage.js
 * Responsibility: Manages client-side storage of 3D WebGL / 2D snapshots and automatically
 * hydrates cart drawer and cart page item thumbnails from localStorage using the unique _design_id.
 */

(function () {
  'use strict';

  const STORAGE_PREFIX = 'fc_orn_snap_';
  const INDEX_KEY = 'fc_orn_index';
  const MAX_STORED_DESIGNS = 15;

  const LaserStorageManager = {
    /**
     * Saves a snapshot data URL into localStorage under a unique design ID
     * @param {string} designId 
     * @param {string} snapshotDataUrl 
     * @param {object} metadata 
     */
    saveSnapshot: function (designId, snapshotDataUrl, metadata) {
      if (!designId || !snapshotDataUrl) return;

      try {
        const payload = {
          id: designId,
          snapshot: snapshotDataUrl,
          metadata: metadata || {},
          timestamp: Date.now()
        };

        // Try writing directly
        this._setWithQuotaManagement(STORAGE_PREFIX + designId, JSON.stringify(payload), designId);
        console.log('[LaserStudio Storage] ✓ Snapshot saved to localStorage for design ID:', designId);
      } catch (err) {
        console.warn('[LaserStudio Storage] Failed to save snapshot to localStorage:', err);
      }
    },

    /**
     * Retrieves snapshot payload for a given design ID
     * @param {string} designId 
     * @returns {string|null} snapshot dataURL
     */
    getSnapshot: function (designId) {
      if (!designId) return null;
      try {
        const raw = localStorage.getItem(STORAGE_PREFIX + designId);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && parsed.snapshot ? parsed.snapshot : null;
      } catch (err) {
        console.warn('[LaserStudio Storage] Error reading snapshot:', err);
        return null;
      }
    },

    /**
     * Deletes a stored design snapshot
     * @param {string} designId 
     */
    deleteSnapshot: function (designId) {
      if (!designId) return;
      try {
        localStorage.removeItem(STORAGE_PREFIX + designId);
        this._removeFromIndex(designId);
      } catch (err) {
        // ignore
      }
    },

    /**
     * Handles safe writes with automatic LRU pruning on QuotaExceededError
     */
    _setWithQuotaManagement: function (key, valueStr, designId) {
      try {
        localStorage.setItem(key, valueStr);
        this._addToIndex(designId);
      } catch (e) {
        if (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014) {
          console.warn('[LaserStudio Storage] Storage quota reached, pruning oldest designs...');
          this._pruneOldest(3);
          try {
            localStorage.setItem(key, valueStr);
            this._addToIndex(designId);
          } catch (retryErr) {
            console.error('[LaserStudio Storage] Critical quota error after pruning:', retryErr);
          }
        }
      }
    },

    /**
     * Adds designId to LRU index
     */
    _addToIndex: function (designId) {
      try {
        let index = this._getIndex();
        index = index.filter((item) => item.id !== designId);
        index.push({ id: designId, timestamp: Date.now() });
        if (index.length > MAX_STORED_DESIGNS) {
          const removed = index.splice(0, index.length - MAX_STORED_DESIGNS);
          removed.forEach((item) => {
            localStorage.removeItem(STORAGE_PREFIX + item.id);
          });
        }
        localStorage.setItem(INDEX_KEY, JSON.stringify(index));
      } catch (e) {
        // ignore
      }
    },

    /**
     * Removes designId from index
     */
    _removeFromIndex: function (designId) {
      try {
        let index = this._getIndex();
        index = index.filter((item) => item.id !== designId);
        localStorage.setItem(INDEX_KEY, JSON.stringify(index));
      } catch (e) {
        // ignore
      }
    },

    /**
     * Retrieves the design index list
     */
    _getIndex: function () {
      try {
        const raw = localStorage.getItem(INDEX_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        return [];
      }
    },

    /**
     * Prunes the oldest N designs
     */
    _pruneOldest: function (count) {
      try {
        let index = this._getIndex();
        if (index.length === 0) return;
        const toPrune = index.splice(0, count || 2);
        toPrune.forEach((item) => {
          localStorage.removeItem(STORAGE_PREFIX + item.id);
        });
        localStorage.setItem(INDEX_KEY, JSON.stringify(index));
      } catch (e) {
        // ignore
      }
    },

    /**
     * Hydrates all cart images with their localStorage snapshots
     * @param {HTMLElement|Document} root 
     */
    hydrateAllSnapshots: function (root) {
      const scope = root || document;

      // 1. Direct match on data-design-id
      const imagesWithDataAttr = scope.querySelectorAll('img[data-design-id], .fc-custom-preview-img');
      imagesWithDataAttr.forEach((img) => {
        const designId = img.dataset.designId;
        if (designId) {
          const snapshot = this.getSnapshot(designId);
          if (snapshot && img.src !== snapshot) {
            img.src = snapshot;
            img.removeAttribute('srcset');
            img.style.objectFit = 'contain';
            img.classList.add('fc-snapshot-hydrated');
          }
        }
      });

      // 2. Fallback: Parse line item rows looking for _design_id in properties
      const cartRows = scope.querySelectorAll('.cart-item, tr[id*="CartItem-"], tr[id*="CartDrawer-Item-"]');
      cartRows.forEach((row) => {
        const img = row.querySelector('.cart-item__image, .cart-item__media img');
        if (!img) return;

        let designId = img.dataset.designId;

        if (!designId) {
          // Check for dt/dd property pairs
          const dds = row.querySelectorAll('dd, .product-option');
          dds.forEach((dd) => {
            const text = dd.innerText || dd.textContent || '';
            const match = text.match(/fc_[a-z0-9_]+/i);
            if (match) {
              designId = match[0].trim();
            }
          });
        }

        if (designId) {
          img.dataset.designId = designId;
          const snapshot = this.getSnapshot(designId);
          if (snapshot && img.src !== snapshot) {
            img.src = snapshot;
            img.removeAttribute('srcset');
            img.style.objectFit = 'contain';
            img.classList.add('fc-snapshot-hydrated');
          }
        }
      });
    },

    /**
     * Initializes observers and auto-hydration hooks
     */
    init: function () {
      const self = this;

      // Hydrate on initial load
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => self.hydrateAllSnapshots());
      } else {
        self.hydrateAllSnapshots();
      }

      // Observe dynamic changes in Cart Drawer & Cart Items
      const observeTarget = document.body;
      if (observeTarget && window.MutationObserver) {
        let debounceTimer = null;
        const observer = new MutationObserver((mutations) => {
          let hasCartMutation = false;
          for (let i = 0; i < mutations.length; i++) {
            const m = mutations[i];
            if (
              m.target &&
              (m.target.id === 'CartDrawer' ||
                m.target.id === 'CartDrawer-CartItems' ||
                m.target.id === 'main-cart-items' ||
                m.target.classList?.contains('drawer__contents') ||
                m.target.classList?.contains('cart-item'))
            ) {
              hasCartMutation = true;
              break;
            }
          }

          if (hasCartMutation) {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
              self.hydrateAllSnapshots();
            }, 50);
          }
        });

        observer.observe(observeTarget, {
          childList: true,
          subtree: true
        });
      }

      // Intercept fetch requests to /cart/add.js, /cart/change.js, /cart/update.js
      if (window.fetch) {
        const originalFetch = window.fetch;
        window.fetch = function () {
          const url = arguments[0];
          const isCartCall =
            typeof url === 'string' &&
            (url.includes('/cart/add') ||
              url.includes('/cart/change') ||
              url.includes('/cart/update') ||
              url.includes('/cart.js'));

          return originalFetch.apply(this, arguments).then((response) => {
            if (isCartCall) {
              setTimeout(() => {
                self.hydrateAllSnapshots();
              }, 150);
            }
            return response;
          });
        };
      }

      // Shopify custom events
      document.addEventListener('cart:updated', () => self.hydrateAllSnapshots());
      document.addEventListener('shopify:section:load', () => self.hydrateAllSnapshots());
    }
  };

  // Expose globally
  window.LaserStorageManager = LaserStorageManager;
  window.LaserStudio = window.LaserStudio || {};
  window.LaserStudio.Storage = LaserStorageManager;

  // Boot hydrator
  LaserStorageManager.init();
})();
